// Global fetch() patch, installed once at app bootstrap (see index.js).
//
// ~90% of this app's API calls are made with raw fetch() scattered across
// individual pages/components (each with its own local `token()` helper),
// not through the shared axios client in services/api.js. Those calls never
// handled a 401, so an expired access token meant data silently stopped
// loading/updating while the page kept rendering as if nothing was wrong.
//
// This patch intercepts every fetch() response for our own API. On a 401 from
// an authenticated call, it attempts a single silent token refresh and retries
// the original request once with the new token — transparent to the calling
// code. If refresh isn't possible (no/invalid refresh token), the user is
// logged out with a clear "session expired" message instead of being left
// looking at a page that has silently gone stale.
import { refreshAccessToken, forceLogout, startProactiveRefresh } from './authRefresh';

const nativeFetch = window.fetch.bind(window);

const AUTH_ENDPOINTS = ['/api/auth/login', '/api/auth/refresh', '/api/auth/verify-login', '/api/2fa/'];

function isAuthEndpoint(url) {
  return typeof url === 'string' && AUTH_ENDPOINTS.some((p) => url.includes(p));
}

function getHeaderValue(headers, name) {
  if (!headers) return undefined;
  if (headers instanceof Headers) return headers.get(name);
  if (Array.isArray(headers)) {
    const hit = headers.find(([k]) => k.toLowerCase() === name.toLowerCase());
    return hit ? hit[1] : undefined;
  }
  // Plain object — check both cases.
  return headers[name] || headers[name.toLowerCase()] || headers[name.toUpperCase()];
}

function withAuthHeader(init, newToken) {
  const next = { ...init };
  if (next.headers instanceof Headers) {
    const merged = new Headers(next.headers);
    merged.set('Authorization', `Bearer ${newToken}`);
    next.headers = merged;
  } else if (Array.isArray(next.headers)) {
    next.headers = [...next.headers.filter(([k]) => k.toLowerCase() !== 'authorization'), ['Authorization', `Bearer ${newToken}`]];
  } else {
    next.headers = { ...(next.headers || {}), Authorization: `Bearer ${newToken}` };
  }
  return next;
}

window.fetch = async function patchedFetch(input, init = {}) {
  const url = typeof input === 'string' ? input : input?.url;
  const response = await nativeFetch(input, init);

  const isApiCall = typeof url === 'string' && url.startsWith('/api');
  const hadAuthHeader = !!getHeaderValue(init?.headers, 'Authorization');
  const alreadyRetried = init && init.__retriedAfterRefresh;

  if (response.status === 401 && isApiCall && hadAuthHeader && !isAuthEndpoint(url) && !alreadyRetried) {
    const newToken = await refreshAccessToken();
    if (newToken) {
      const retryInit = withAuthHeader(init, newToken);
      retryInit.__retriedAfterRefresh = true;
      return nativeFetch(input, retryInit);
    }
    // refreshAccessToken() already logged the user out on failure.
  }

  return response;
};

startProactiveRefresh();
